// DetailPage.js

import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import { StickyNote, Plus } from "lucide-react";
import UnitComponentsPanel from "./UnitComponentsPanel";
import NotesWindow from "../components/NotesWindow";
import Modal from "../components/Modal";
import InventoryImages from "../components/InventoryImages";
import { getStatusBadge } from "../scripts/colorCodes";

// --- Status list used by ColorCode dropdown (matches your status.js)
const STATUS_BY_CODE = {
  0: { bg: '#FFFFFF', label: 'UNASSIGNED' },
  1: { bg: '#C757FF', label: 'SOLD' },
  2: { bg: '#FFD966', label: 'RENTED OUT' },
  3: { bg: '#69e7a2ff', label: 'IN STOCK - CALGARY' },
  4: { bg: '#92D050', label: 'IN TRANSIT TO CALGARY' },
  5: { bg: '#92D050', label: 'IN TRANSIT TO CUSTOMER' },
  6: { bg: '#FF0000', label: 'ALERT' },
  7: { bg: '#4183e6ff', label: 'TO ORDER' },
  8: { bg: '#FFCCFF', label: 'ON ORDER' },
  9: { bg: '#00B050', label: 'STOCK & PDI - IN CALGARY' },
  10:{ bg: '#ce951bff', label: 'DEMO' }, 
  11:{ bg: '#1b54ceff', label: 'CHASSIS ALLOCATION SLOT' },
  12:{ bg: '#0a2479ff', label: 'READY AT FACTORY' },
  13: { bg: '#FF0000', label: 'IN TRANSIT - BROKE DOWN' },
};

const COLOR_CODE_OPTIONS = Object.entries(STATUS_BY_CODE)
  .map(([code, v]) => ({ v: String(code), label: `${code} - ${v.label}` }))
  .sort((a, b) => Number(a.v) - Number(b.v));

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "refuse", label: "Refuse" },
  { v: "municipalequip", label: "Municipal Equipment" },
  { v: "municipalparts", label: "Municipal Parts" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer Water" },
  { v: "sweeper", label: "Sweeper" },
  { v: "mrc", label: "MRC" },
  { v: "rail", label: "Rail" },
  { v: "snow", label: "Sander, Spreader, Plows"},
  { v: "tireshop", label: "Tire Shop"},
  { v: "foundations", label: "Foundations"},
  { v: "agriculture", label: "Agriculture"},
  { v: "carts", label: "Carts"},
  { v: "other", label: "Other" },
];

// columns that are DATE in SQLite
const DATE_FIELDS = new Set([
  "customer_po_date",
  "factory_order_date",
  "chassis_build_date",
  "chassis_arrive_eta",
  "ready_at_factory",
  "arrive_sna",
  "to_customer",
  "body_status_update",
  "chassis_status_update",
]);

// fields that should be treated as numeric/currency TEXT, NOT booleans
const CURRENCY_FIELDS = new Set([
  "exchange",
  "misc_charges",
  "misc_hours",
  "misc_mileage",
  "misc_exchange",
  "misc_addons",
  "misc_repairs",
  "pdi_charges",
  "freight_charges",
  "fright_charges",
  "body_net_sna",
  "body_cost",
  "chassis_net_sna",
  "chassis_cost",
  "total_net_sna",
  "total_cost",
  "total_sell",
  "surcharge",
  "tarrif",
]);

// flags rendered as checkboxes
const CHECKBOX_FIELDS = new Set([
  "is_chassis",
  "is_body",
  "is_attachment",
  "is_equipment",
  "is_stock",
  "is_demo",
  "is_rental",
  "alert",
]);

// GROUPS — reorganised so body and chassis are first-class top-level sections,
// with order/financial/lifecycle context grouped distinctly. Each group also
// carries an `accent` colour and `icon` glyph so the new sleek GroupCard
// header can render a coloured badge instead of a plain bold label.
const GROUPS = [
  {
    title: "Customer & Order",
    accent: "#2563eb",
    icon: "🧾",
    fields: [
      { k: "customer",                    label: "Customer Sold To" },
      { k: "customer_po",                 label: "Customer PO" },
      { k: "sna_customer_po",             label: "SNA Customer PO" },
      { k: "customer_po_date",            label: "Customer PO Date" },
      { k: "rowo",                        label: "RO / WO #" },
      { k: "stock_po",                    label: "Stock PO" },
      { k: "tft_po",                      label: "TFT Alco PO" },
      { k: "factory_order_date",          label: "Factory Order Date" },
      { k: "factory_production_so_order", label: "Factory Production SO Order" },
      { k: "factory_wo",                  label: "Factory WO" },
      { k: "sent_so",                     label: "Sent SO" },
      { k: "sign_so",                     label: "Sign SO" },
      { k: "revd_eng",                    label: "Rev'd Engineering" },
      { k: "sna_body_chassis_po",         label: "SNA Body/Chassis PO" },
      { k: "riv_(TEXT)",                  label: "RIV" },
    ],
  },
  {
    title: "Body",
    accent: "#16a34a",
    icon: "🚛",
    fields: [
      { k: "body_make_model",      label: "Body Make & Model" },
      { k: "body_vin",             label: "Body VIN / Serial" },
      { k: "body_status_update",   label: "Body Status Update" },
      { k: "body_net_sna",         label: "Body Cost" },
    ],
  },
  {
    title: "Chassis",
    accent: "#9333ea",
    icon: "🚚",
    fields: [
      { k: "chassis_make_model",     label: "Chassis Make & Model" },
      { k: "chassis_vin",            label: "Chassis VIN" },
      { k: "chassis_build_date",     label: "Chassis Build Date" },
      { k: "chassis_arrive_eta",     label: "Chassis Arrive ETA" },
      { k: "chassis_status_update",  label: "Chassis Status Update" },
      { k: "chassis_net_sna",        label: "Chassis Cost" },
    ],
  },
  {
    title: "Lifecycle",
    accent: "#0891b2",
    icon: "📅",
    fields: [
      { k: "ready_at_factory",  label: "Ready at Factory" },
      { k: "arrive_sna",        label: "Arrive at SNA (Calgary)" },
      { k: "factory_pick_up",   label: "Factory Pickup (Driver)" },
      { k: "factory_del_sna",   label: "Delivered to SNA (Driver)" },
      { k: "to_customer",       label: "Delivered to Customer" },
      { k: "unit_location",     label: "Current Location" },
    ],
  },
  {
    title: "Status & Classification",
    accent: "#d97706",
    icon: "🏷️",
    fields: [
      { k: "color_code",      label: "Status" },
      { k: "unit_status",     label: "Unit Status (text)" },
      { k: "unit_category",   label: "Category" },
      { k: "type",            label: "Unit Type" },
      { k: "alert",           label: "Alert" },
      { k: "is_chassis",      label: "Chassis" },
      { k: "is_body",         label: "Body" },
      { k: "is_attachment",   label: "Attachment" },
      { k: "is_equipment",    label: "Equipment" },
      { k: "is_stock",        label: "Stock" },
      { k: "is_demo",         label: "Demo" },
      { k: "is_rental",       label: "Rental" },
    ],
  },
  {
    title: "Finance",
    accent: "#0d9488",
    icon: "💰",
    fields: [
      { k: "total_sell",      label: "Total Sell Price" },
      { k: "total_net_sna",   label: "Total Cost" },
      { k: "fright_charges",  label: "Freight Cost" },
      { k: "pdi_charges",     label: "PDI" },
      { k: "surcharge",       label: "Surcharge" },
      { k: "tarrif",          label: "Tariff" },
      { k: "exchange",        label: "Exchange" },
      { k: "misc_charges",    label: "Misc Charges" },
      { k: "misc_hours",      label: "Misc Hours" },
      { k: "misc_mileage",    label: "Misc KM" },
      { k: "misc_exchange",   label: "Misc Exchange" },
      { k: "misc_addons",     label: "Misc Add-ons" },
      { k: "misc_repairs",    label: "Misc Repairs" },
    ],
  },
  {
    title: "Notes",
    accent: "#64748b",
    icon: "📝",
    fields: [
      { k: "notes",  label: "Notes" },
      { k: "notes2", label: "Notes 2" },
    ],
  },
];

// Match DB keys even if user typed slightly different (case/spacing/underscores)
function findKey(draft, wantedKey) {
  if (!draft) return null;
  if (Object.prototype.hasOwnProperty.call(draft, wantedKey)) return wantedKey;

  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
  const w = norm(wantedKey);
  for (const k of Object.keys(draft)) {
    if (norm(k) === w) return k;
  }
  return null;
}

function isBoolishValue(v) {
  if (v === 0 || v === 1 || v === true || v === false) return true;
  const s = String(v ?? "").toLowerCase();
  return s === "true" || s === "false" || s === "0" || s === "1" || s === "yes" || s === "no";
}

function toDateInputValue(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return "";
}

function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function getUnitKind(record) {
  const isChassis = isTruthyFlag(record.is_chassis);
  const isBody = isTruthyFlag(record.is_body);
  const isAttachment = isTruthyFlag(record.is_attachment);
  const isEquipment = isTruthyFlag(record.is_equipment);

  if (isChassis || isBody || isAttachment || isEquipment) {
    const parts = [];
    if (isChassis) parts.push("Chassis");
    if (isBody) parts.push("Body");
    if (isAttachment) parts.push("Attachment");
    if (isEquipment) parts.push("Equipment");
    return parts.join(" + ");
  }

  const hasChassis = !!(record.chassis_vin && String(record.chassis_vin).trim());
  const hasBody = !!(record.body_vin && String(record.body_vin).trim());
  if (hasChassis && hasBody) return "Body + Chassis";
  if (hasChassis) return "Chassis";
  if (hasBody) return "Body";
  return "Unit";
}

function Field({ label, fieldKey, value, onChange }) {
  // ? ColorCode dropdown from STATUS_BY_CODE
  if (fieldKey === "color_code") {
    const cur = value === null || value === undefined ? "" : String(value).trim();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          {COLOR_CODE_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // ? unit_category dropdown
  if (fieldKey === "unit_category") {
    const v = (value ?? "").toString().toLowerCase();
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>{label}</div>
        <select className="input" value={v} onChange={(e) => onChange(e.target.value)}>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // ? date picker
  if (DATE_FIELDS.has(fieldKey)) {
    const raw = value ?? "";
    const v = toDateInputValue(raw);
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>{label}</div>
        <input type="date" className="input" value={v} onChange={(e) => onChange(e.target.value || "")} />
        {raw && !v && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Current (raw): {String(raw)}
          </div>
        )}
      </div>
    );
  }

  // ? finance fields always text
  if (CURRENCY_FIELDS.has(fieldKey)) {
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>{label}</div>
        <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }

  // ? all checkboxes (is_* + alert)
  if (CHECKBOX_FIELDS.has(fieldKey)) {
    const checked = isTruthyFlag(value);

    return (
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked ? "1" : "0")} />
        {label}
      </label>
    );
  }

  // generic bool-ish (0/1/true/false/yes/no) - keep as dropdown for other fields
  if (isBoolishValue(value)) {
    const s = String(value ?? "").toLowerCase();
    const cur = s === "1" || s === "true" || s === "yes" ? "1" : s === "0" || s === "false" || s === "no" ? "0" : "";
    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>{label}</div>
        <select className="input" value={cur} onChange={(e) => onChange(e.target.value)}>
          <option value="">(blank)</option>
          <option value="1">true</option>
          <option value="0">false</option>
        </select>
      </div>
    );
  }

  // default text input
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{label}</div>
      <input className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SortableGroup({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // ? pass handle props down; do NOT attach listeners to wrapper
  const handleProps = { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} style={style}>
      {children(handleProps)}
    </div>
  );
}



function GroupCard({ title, items, accent, icon, draft, setDraft, handleProps }) {
  const isClassification = title === "Status & Classification";
  const accentColor = accent || "#64748b";

  const wrapperStyle = isClassification
    ? { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      };

  return (
    <div
      className="card"
      style={{
        minWidth: 0,
        padding: 0,
        overflow: "hidden",
        borderTop: `3px solid ${accentColor}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 14px",
          background: `linear-gradient(180deg, ${accentColor}10, transparent)`,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: `${accentColor}1f`,
              color: accentColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              flex: "0 0 auto",
            }}
            aria-hidden
          >
            {icon || "•"}
          </div>
          <div
            style={{
              fontWeight: 700,
              letterSpacing: 0.3,
              fontSize: 13,
              textTransform: "uppercase",
              color: accentColor,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
        </div>

        <button
          type="button"
          {...handleProps}
          style={{
            cursor: "grab",
            userSelect: "none",
            border: "1px solid rgba(0,0,0,0.12)",
            background: "rgba(255,255,255,0.65)",
            borderRadius: 8,
            padding: "2px 8px",
            lineHeight: 1,
            color: "#64748b",
            fontSize: 14,
            flex: "0 0 auto",
          }}
          title="Drag to reorder"
          aria-label={`Drag ${title}`}
          onMouseDown={(e) => e.preventDefault()}
        >
          ⋮⋮
        </button>
      </div>

      <div style={{ padding: 14, ...wrapperStyle }}>
        {items.map(({ label, key }) => (
          <Field
            key={key}
            label={label}
            fieldKey={key}
            value={draft?.[key]}
            onChange={(v) => setDraft({ ...draft, [key]: v })}
          />
        ))}
      </div>
    </div>
  );
}


function AddNoteModal({ inventoryId, onClose, onSaved }) {
  const [body, setBody] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const save = async () => {
    const text = String(body || "").trim();
    if (!text) { setErr("Enter a note."); return; }
    setSaving(true); setErr("");
    try {
      await api.post(`/inventory/${inventoryId}/notes`, { body: text });
      onSaved?.(); onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to save note.");
    } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginBottom: 12 }}>Add Note</h2>
      <textarea className="input" value={body} onChange={e => setBody(e.target.value)}
        rows={6} placeholder="Write a note…"
        style={{ width: "100%", resize: "vertical", fontSize: 14 }}
        onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") save(); }}
        disabled={saving} autoFocus />
      {err && <div style={{ marginTop: 8, color: "#ef4444", fontSize: 13 }}>{err}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
        <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Note"}</button>
      </div>
    </Modal>
  );
}

export default function DetailPage() {
  const { table, id } = useParams();
  const nav = useNavigate();

  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [related, setRelated] = useState([]);
  const [relatedErr, setRelatedErr] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);


  
  // ? load record
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/${table}/${id}`);
        if (!alive) return;
        setDraft(data);
      } catch (e) {
        console.error("Load detail failed", e);
        if (alive) setMsg("Failed to load");
      }
    })();
    return () => { alive = false; };
  }, [table, id]);

  // ? related records
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setRelatedErr("");
        const { data } = await api.get(`/related/${table}/${id}`);
        if (!alive) return;
        setRelated(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Load related failed", e);
        if (!alive) return;
        setRelated([]);
        setRelatedErr("Failed to load related records");
      }
    })();
    return () => { alive = false; };
  }, [table, id]);

  // ? DEFINE THESE FIRST (before orderedItems)
  const resolvedGroups = useMemo(() => {
    if (!draft) return [];
    return GROUPS.map((g) => {
      const items = [];
      for (const f of g.fields) {
        const resolvedKey = findKey(draft, f.k);
        if (!resolvedKey) continue;
        items.push({ label: f.label, key: resolvedKey });
      }
      return { title: g.title, accent: g.accent, icon: g.icon, items };
    }).filter((g) => g.items.length > 0);
  }, [draft]);

  const remaining = useMemo(() => {
    if (!draft) return [];
    const used = new Set(["id"]);
    for (const g of resolvedGroups) for (const it of g.items) used.add(it.key);

    return Object.keys(draft)
      .filter((k) => !used.has(k))
      .sort((a, b) => a.localeCompare(b));
  }, [draft, resolvedGroups]);

  // ? THEN your group ordering state + orderedItems
  const DEFAULT_GROUP_ORDER = GROUPS.map((g) => g.title).concat(["__remaining__"]);
  const ORDER_KEY = `detail_group_order:${table}`;

  const [groupOrder, setGroupOrder] = useState(() => {
    try {
      const raw = sessionStorage.getItem(ORDER_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {}
    return DEFAULT_GROUP_ORDER;
  });

useEffect(() => {
  try {
    sessionStorage.setItem(ORDER_KEY, JSON.stringify(groupOrder));
  } catch {}
}, [groupOrder, ORDER_KEY]);

const orderedItems = useMemo(() => {
  const byId = new Map();

  // resolvedGroups come from your existing useMemo
  for (const g of resolvedGroups) byId.set(g.title, { type: "group", g });

  // Remaining block
  byId.set("__remaining__", { type: "remaining" });

  // Keep only items that exist (e.g. if a group has 0 fields it won-t be in resolvedGroups)
  const existing = groupOrder.filter((id) => byId.has(id));

  // Append any new groups that exist but aren't in order yet
  for (const [id] of byId) if (!existing.includes(id)) existing.push(id);

  return existing.map((id) => ({ id, ...byId.get(id) }));
}, [groupOrder, resolvedGroups, remaining]);

const sensors = useSensors(
  useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
);

function onDragEnd(event) {
  const { active, over } = event;
  if (!over) return;
  if (active.id === over.id) return;

  setGroupOrder((prev) => {
    const oldIndex = prev.indexOf(active.id);
    const newIndex = prev.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return prev;
    return arrayMove(prev, oldIndex, newIndex);
  });
}

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/${table}/${id}`);
        if (!alive) return;
        setDraft(data);
      } catch (e) {
        console.error("Load detail failed", e);
        if (alive) setMsg("Failed to load");
      }
    })();
    return () => {
      alive = false;
    };
  }, [table, id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setRelatedErr("");
        const { data } = await api.get(`/related/${table}/${id}`);
        if (!alive) return;
        setRelated(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Load related failed", e);
        if (!alive) return;
        setRelated([]);
        setRelatedErr("Failed to load related records");
      }
    })();
    return () => {
      alive = false;
    };
  }, [table, id]);

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      const payload = { ...draft };
      delete payload.id;

      // keep alert saved as TEXT "0"/"1"
      if (payload.alert !== undefined) payload.alert = isTruthyFlag(payload.alert) ? "1" : "0";

      const { data: res } = await api.put(`/${table}/${id}`, payload);
      setMsg(`Saved (${res.updated || 0})`);
    } catch (e) {
      console.error("Save failed", e);
      setMsg("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRecord = async () => {
    if (!window.confirm(`Permanently delete ${table} #${id}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/${table}/${id}`);
      nav(-1);
    } catch (e) {
      setMsg(e?.response?.data?.error || 'Delete failed');
      setBusy(false);
    }
  };

  const canSyncWebsite = ["all_inventory", "stock_inventory", "used_inventory", "sold_inventory"].includes(table);

  const syncToWebsite = async () => {
    if (!canSyncWebsite) return;
    setSyncBusy(true);
    setMsg("");
    try {
      await api.post(`/website-sync/${table}/${id}`);
      setMsg("Website sync queued");
    } catch (e) {
      console.error("Website sync failed", e);
      setMsg(e?.response?.data?.error || "Website sync failed");
    } finally {
      setSyncBusy(false);
    }
  };


  if (!draft) return <div className="container">Loading-</div>;

  const statusBadge = getStatusBadge(draft.color_code);
  const heroAccent = statusBadge?.bg || "#2563eb";
  const customerLabel = draft.customer || draft.customer_sold_to || "—";
  const bodyLabel = draft.body_make_model || (draft.body_vin ? `VIN ${draft.body_vin}` : "");
  const chassisLabel = draft.chassis_make_model || (draft.chassis_vin ? `VIN ${draft.chassis_vin}` : "");
  const stockLabel = draft.stock_po ? `Stock ${draft.stock_po}` : "";
  const roLabel = draft.rowo ? `RO/WO ${draft.rowo}` : `#${id}`;
  const heroChips = [
    bodyLabel && { icon: "🚛", label: "Body", text: bodyLabel, color: "#16a34a" },
    chassisLabel && { icon: "🚚", label: "Chassis", text: chassisLabel, color: "#9333ea" },
    stockLabel && { icon: "📦", label: "Stock PO", text: stockLabel, color: "#0891b2" },
  ].filter(Boolean);

  return (
    <div className="container">
      <div
        className="card"
        style={{
          padding: 0,
          overflow: "hidden",
          borderTop: `4px solid ${heroAccent}`,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            background: `linear-gradient(135deg, ${heroAccent}14, transparent 60%)`,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "flex-start",
          }}
        >
          <div style={{ minWidth: 0, flex: "1 1 360px" }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: "#64748b",
                marginBottom: 4,
              }}
            >
              {table.replace(/_/g, " ")} · #{id}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, fontWeight: 800, letterSpacing: 0.3, fontSize: 22 }}>
                {roLabel}
              </h2>
              {statusBadge && (
                <span
                  style={{
                    background: statusBadge.bg,
                    color: statusBadge.fg,
                    padding: "4px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusBadge.label}
                </span>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#475569", fontWeight: 500 }}>
              {customerLabel}
            </div>

            {heroChips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                {heroChips.map((chip) => (
                  <div
                    key={chip.label}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      background: `${chip.color}14`,
                      border: `1px solid ${chip.color}33`,
                      borderRadius: 999,
                      fontSize: 12,
                      color: "#1e293b",
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 13 }}>{chip.icon}</span>
                    <span style={{ color: chip.color, fontWeight: 700, letterSpacing: 0.3 }}>
                      {chip.label}
                    </span>
                    <span style={{ color: "#475569" }}>{chip.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <button className="btn-outline" type="button" onClick={() => setNotesOpen(true)}>
            <StickyNote size={16} style={{ marginRight: 8 }} />
            Notes
          </button>

          <button className="btn-outline" type="button" onClick={() => setAddNoteOpen(true)}>
            <Plus size={16} style={{ marginRight: 8 }} />
            Add Note
          </button>

    <button className="btn-outline" type="button" onClick={() => nav(`/pdi/${id}?table=${table}`)}>
      PDI
    </button>

          {canSyncWebsite && (
            <button className="btn-outline" type="button" onClick={syncToWebsite} disabled={syncBusy}>
              {syncBusy ? "Syncing..." : "Sync to Website"}
            </button>
          )}

          <details style={{ position: 'relative', display: 'inline-block' }}>
            <summary style={{ cursor: 'pointer', listStyle: 'none', padding: '6px 12px', border: '1px solid var(--btn-outline)', borderRadius: 8, fontSize: 13, background: 'var(--card-bg)', color: 'var(--fg)' }}>
              ···
            </summary>
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.15)', zIndex: 100, minWidth: 180, padding: 4 }}>
              <button
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b' }}
                onClick={() => { document.activeElement.blur(); deleteRecord(); }}
                disabled={busy}
              >
                Delete Record…
              </button>
            </div>
          </details>

          <button className="btn" onClick={save} disabled={busy}>
            {busy ? "Saving?" : "Submit"}
          </button>
        </div>
        </div>
      </div>

      {msg && <div className="muted">{msg}</div>}

      <div className="card" style={{ minWidth: 0, marginTop: 12 }}>
        <div style={{ fontWeight: 800, letterSpacing: 0.2, marginBottom: 10 }}>Related Records (same RO/WO or Stock PO)</div>

        {relatedErr && <div className="muted">{relatedErr}</div>}
        {!relatedErr && related.length === 0 && <div className="muted">No related records found.</div>}

        {related.length > 0 && (
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            {related.map((r) => {
              const kind = getUnitKind(r);
              const label = `${kind || "Unit"} - ID #${r.id} - RO/WO ${r.rowo || "-"} - Stock ${r.stock_po || "-"}`;

              return (
                <button key={r.id} className="btn" type="button" onClick={() => nav(`/detail/${table}/${r.id}`)}>
                  Open: {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Unit Components Panel — only for all_inventory */}
      {table === 'all_inventory' && <UnitComponentsPanel unitId={id} />}

      {/* Inventory Images */}
      <InventoryImages inventoryId={id} tableName={table} />

      {/* ? IMPORTANT: make cards "fit content" (no equal height stretch) */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
  <SortableContext
  items={orderedItems.map((x) => x.id)}
  strategy={verticalListSortingStrategy}
>
  <div
    style={{
      columnWidth: 360,
      columnGap: 12,
      alignItems: "start",
    }}
  >
    {orderedItems.map((item) => {
      if (item.type === "group") {
        return (
          <SortableGroup key={item.id} id={item.id}>
            {(handleProps) => (
              <div
                style={{
                  breakInside: "avoid",
                  WebkitColumnBreakInside: "avoid",
                  marginBottom: 12,
                  width: "100%",
                  display: "inline-block",
                }}
              >
                <GroupCard
                  title={item.g.title}
                  items={item.g.items}
                  accent={item.g.accent}
                  icon={item.g.icon}
                  draft={draft}
                  setDraft={setDraft}
                  handleProps={handleProps}
                />
              </div>
            )}
          </SortableGroup>
          );
        }

        // remaining
        return (
          <SortableGroup key={item.id} id={item.id}>
  {(handleProps) => (
    <div className="card" style={{ minWidth: 0 }}>
      <div
        style={{
          breakInside: "avoid",
                WebkitColumnBreakInside: "avoid",
                marginBottom: 12,
                width: "100%",
                display: "inline-block",
        }}
      >
        <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>Remaining Fields</div>
        <button
          type="button"
          {...handleProps}
          style={{
            cursor: "grab",
            userSelect: "none",
            border: "1px solid rgba(0,0,0,0.15)",
            background: "#f5f5f5",
            borderRadius: 8,
            padding: "4px 8px",
            lineHeight: 1,
          }}
          title="Drag to reorder"
          aria-label="Drag Remaining Fields"
          onMouseDown={(e) => e.preventDefault()}
        >
          ?
        </button>
      </div>

      {remaining.length === 0 ? (
        <div className="muted">Nothing extra.</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {remaining.map((k) => (
            <Field
              key={k}
              label={k}
              fieldKey={k}
              value={draft?.[k]}
              onChange={(v) => setDraft({ ...draft, [k]: v })}
            />
          ))}
        </div>
      )}
    </div>
  )}
</SortableGroup>

        );
      })}
    </div>
  </SortableContext>
</DndContext>

      {notesOpen && (
        <NotesWindow
          open={notesOpen}
          inventoryId={id}
          inventoryTitle={`${table} #${draft?.rowo || draft?.stock_po || id}`}
          onClose={() => setNotesOpen(false)}
          onSeen={() => {}}
        />
      )}
      {addNoteOpen && (
        <AddNoteModal
          inventoryId={id}
          onClose={() => setAddNoteOpen(false)}
          onSaved={() => setAddNoteOpen(false)}
        />
      )}
    </div>
  );
}
